/**
 * The video carousel inside the "Reviews & videos" section.
 *
 * The track is a plain scroll-snap row, so it swipes natively on touch and
 * keeps working without this script. The element only adds what CSS cannot do:
 * arrows that page through the row and know when they have run out of track,
 * and swapping a card's poster for the real video on the first click, so no
 * video is fetched until someone asks for one.
 */
class ReviewVideos extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #previous = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {ResizeObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    this.#previous = this.querySelector('[data-scroll="previous"]');
    this.#next = this.querySelector('[data-scroll="next"]');

    this.addEventListener('click', this.#onClick);

    if (!this.#track) return;

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#observer = new ResizeObserver(this.#syncArrows);
    this.#observer.observe(this.#track);
    this.#syncArrows();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('click', this.#onClick);
  }

  /** How far one arrow press moves the track: one card plus its gap. */
  get #page() {
    const card = this.#track?.querySelector('[data-card]');
    if (!(card instanceof HTMLElement) || !this.#track) return 0;

    const gap = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    return card.offsetWidth + gap;
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const arrow = target.closest('[data-scroll]');
    if (arrow instanceof HTMLElement) {
      const direction = arrow.dataset.scroll === 'previous' ? -1 : 1;
      this.#track?.scrollBy({ left: this.#page * direction, behavior: 'smooth' });
      return;
    }

    const play = target.closest('[data-play]');
    if (play instanceof HTMLElement) this.#play(play);
  };

  /**
   * Replaces a card's poster with the video it holds in a template. The markup
   * only exists as a template until now, so nothing was loaded for cards the
   * visitor never opened.
   * @param {HTMLElement} button - The play button that was pressed.
   */
  #play(button) {
    const card = button.closest('[data-card]');
    const template = card?.querySelector('template');

    if (!(card instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    const media = card.querySelector('[data-media]');
    if (!(media instanceof HTMLElement)) return;

    media.replaceChildren(template.content.cloneNode(true));
    card.setAttribute('data-playing', '');

    // A Shopify-hosted video is a real <video>, so start it; an embed autoplays
    // through its own URL parameters.
    const video = media.querySelector('video');
    if (video instanceof HTMLVideoElement) video.play().catch(() => {});
  }

  #onScroll = () => this.#syncArrows();

  #syncArrows = () => {
    const track = this.#track;
    if (!track) return;

    const overflow = track.scrollWidth - track.clientWidth;
    const start = track.scrollLeft <= 1;
    const end = track.scrollLeft >= overflow - 1;

    this.toggleAttribute('data-scrollable', overflow > 1);

    if (this.#previous) this.#previous.disabled = start;
    if (this.#next) this.#next.disabled = end;
  };
}

if (!customElements.get('review-videos')) {
  customElements.define('review-videos', ReviewVideos);
}
